import I18n from 'i18n-js';
import React from 'react';
import { identity, pickBy, get, omit, isEqual } from 'lodash';

import FollowModal from './follow_modal';
import FollowButton from './follow_button';
import LoggedOutFollowButton from './logged_out_follow_button';
import { IFollowParams } from '@reverbdotcom/commons';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';

import RouterHistory from '@reverbdotcom/commons/src/cms/lib/router_history';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { trackEvent, MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';

import FollowService from './follow_service';
import { IFindFollowResponse } from '../../../schemas/core-schema';
import bind from '@reverbdotcom/commons/src/bind';
import locationUtil from '../../shared/location_util';

import { USED_CONDITION, FOLLOW_PROMPT_PARAM } from '../../shared/constants';

const FEED_HREF = '/my/feed';
const SINGLE_FOLLOW_PARAM = 1;
const TWO_FOLLOW_PARAMS = 2;
const FOLLOWING_SUBCATEGORY_WITH_NO_FILTERS = 3;
const FOLLOW_BUTTON = 'FollowButton';
const FOLLOW_BUTTON_IN_BANNER = 'FollowButtonInBanner';

interface ExternalProps {
  headerLabel?: string;

  location?: any; // DEPRECATED
  routeParams?: any; // DEPRECATED

  withPriceFilters?: boolean;
  withUsedFilter?: boolean;
  unstyled?: boolean;
  outlined?: boolean;
  followParams?: IFollowParams;
  addedText?: string;
  addText?: string;
  disabled?: boolean;
  disabledTooltipText?: string;
  renderWithBanner?: boolean;

  /** Use on secondary instance if multiple appear on same page */
  ignoreFollowPrompt?: boolean;
}

type IProps = ExternalProps & IUserContext;

interface State {
  modalOpen: boolean;
  addGearAlert: boolean;
  usedOnly: boolean;
  priceMin?: number;
  priceMax?: number;
  findFollowResponse?: IFindFollowResponse;
  loaded: boolean;
  filterByPrice: boolean;
}

export class FollowButtonContainer extends React.Component<IProps, State> {
  public static defaultProps: Partial<IProps> = {
    location: {},
  };

  constructor(props) {
    super(props);

    const modalOpen = !!props.followParams[FOLLOW_PROMPT_PARAM] && !props.ignoreFollowPrompt;

    this.state = {
      modalOpen,
      addGearAlert: true,
      usedOnly: false,
      priceMin: null,
      priceMax: null,
      loaded: false,
      filterByPrice: false,
    };
  }

  componentDidMount() {
    this.loadExistingFollowAndCount();
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  componentDidUpdate(prevProps) {
    // Don't refetch follow if it's just internal state that's changed
    const notChanged = isEqual(prevProps, this.props);
    if (notChanged) { return; }
    this.loadExistingFollowAndCount();

    this.setState({ modalOpen: !!this.props.followParams[FOLLOW_PROMPT_PARAM] });
  }

  async loadExistingFollowAndCount() {
    const params = this.buildParams();
    const findFollowResponse = await FollowService.fetch(params);
    if (!findFollowResponse?.query_params) {
      // this is returning blank for some scrapers for some reason
      // https://sentry.io/organizations/reverb-llc/issues/2949522373/
      return;
    }
    const queryParams = JSON.parse(findFollowResponse.query_params);
    const usedOnly = isEqual(queryParams.condition, [USED_CONDITION]);
    const priceMax = queryParams.price_max;
    const priceMin = queryParams.price_min;
    const modalOpen = this.autoOpenForFollow(findFollowResponse);

    let addGearAlert = true;
    if (findFollowResponse.following && !findFollowResponse.alert) {
      addGearAlert = false;
    }

    this.setState({
      findFollowResponse,
      modalOpen,
      addGearAlert,
      usedOnly,
      priceMax,
      priceMin,
      loaded: true,
      filterByPrice: !!(priceMin || priceMax),
    });
  }

  autoOpenForFollow(follow) {
    if (!this.state.modalOpen || this.props.disabled) {
      return false;
    }

    return !!(follow && !follow.following && get(follow, '_links.self.href'));
  }

  @bind
  async handleSubmit() {
    this.closeModal();

    const paramsWithFormValues = {

      ...this.buildParams(),
      ...this.buildFormValues(),
    };

    const findFollowResponse = await FollowService.add(
      paramsWithFormValues,
    );

    if (get(findFollowResponse, 'id')) {
      trackEvent({
        eventName: MParticleEventName.AddedFollow,
        source: this.props.renderWithBanner ? FOLLOW_BUTTON_IN_BANNER : FOLLOW_BUTTON,
        followId: findFollowResponse.id,
        followType: this.getTranslationKey(),
      });
    }

    this.setState({ findFollowResponse });
  }

  disabled() {
    if (this.props.disabled) {
      return true;
    }

    return !(this.state.loaded && get(this.state.findFollowResponse, '_links.self.href'));
  }

  buildFormValues() {
    const { priceMin, priceMax, usedOnly, addGearAlert } = this.state;

    const formValues: any = {
      price_min: priceMin,
      price_max: priceMax,
      digest_enabled: addGearAlert,
    };

    if (usedOnly) {
      formValues.condition = USED_CONDITION;
    }

    return pickBy(formValues, identity);
  }

  buildParams() {
    const params = this.props.followParams || ({

      ...this.props.location.query,
      ...this.props.routeParams,
    });

    if (get(this.state, 'findFollowResponse.following')) {
      params.id = this.state.findFollowResponse.id;
    }

    return params;
  }

  @bind
  closeModal() {
    const newParams = locationUtil.queryParams();
    if (!newParams[FOLLOW_PROMPT_PARAM]) {
      this.setState({ modalOpen: false });
      return;
    }

    // Follow API always thinks queries with unknown params are followable.
    // Strip off this no-op param so we don't popup the modal on e.g. product type pages
    const updatedParams = omit(newParams, FOLLOW_PROMPT_PARAM);
    RouterHistory.replace({
      pathname: locationUtil.pathname(),
      query: updatedParams,
    });
  }

  @bind
  openModal() {
    if (this.disabled()) { return; }
    this.setState({ modalOpen: true });
  }

  getTranslationKey() {
    const params = this.buildParams();
    const followParams = Object.keys(params).filter(k => !!params[k]); // avoid counting empty params

    if (followParams.includes('query')) return 'search';

    if (followParams.includes('product_type') &&
      followParams.includes('category') &&
      followParams.includes('id') &&
      followParams.length === FOLLOWING_SUBCATEGORY_WITH_NO_FILTERS
    ) return 'category';

    if (followParams.includes('product_type') &&
      followParams.includes('category') &&
      followParams.length === TWO_FOLLOW_PARAMS
    ) return 'category';

    if (followParams.length === SINGLE_FOLLOW_PARAM ||
      followParams.length === TWO_FOLLOW_PARAMS && followParams.includes('id')) {
      if (followParams.includes('product_type')) return 'category';
      if (followParams.includes('comparison_shopping_page_slug')) return 'product';
      if (followParams.includes('shop') ||
        followParams.includes('shop_id')) return 'shop';
      if (followParams.includes('curated_set_slug') ||
        followParams.includes('curated_set_id')) return 'collection';
      if (followParams.includes('brand_slug') ||
        followParams.includes('make')) return 'brand';
    }

    return 'search';
  }

  getFollowType() {
    return I18n.t(`discovery.addFollowToFeed.followType.${this.getTranslationKey()}`);
  }

  getBannerFollowType() {
    return I18n.t(`discovery.addFollowToFeed.followBannerType.${this.getTranslationKey()}`);
  }

  buttonText() {
    return I18n.t(
      'discovery.addFollowToFeed.followThis',
      { type: this.getFollowType() },
    );
  }

  headerLabel() {
    return (
      this.props.headerLabel ||
      I18n.t(
        'discovery.addFollowToFeed.followModalTitle',
        { type: this.getFollowType().toLowerCase() },
      )
    );
  }

  isFollowing() {
    return get(this.state, 'findFollowResponse.following', false);
  }

  readableTitle() {
    return get(this.state, 'findFollowResponse.display');
  }

  hidden() {
    return get(this.state, 'findFollowResponse.hidden', false);
  }

  @bind
  onModalUpdate(state) {
    const keys = ['usedOnly', 'addGearAlert', 'priceMin', 'priceMax', 'filterByPrice'];
    const providedState: any = {};
    keys.forEach((key) => {
      if (state.hasOwnProperty(key)) {
        providedState[key] = state[key];
      }
    });
    if (state.filterByPrice === false) {
      providedState.priceMin = null;
      providedState.priceMax = null;
    }
    this.setState(providedState);
  }

  renderButton() {
    if (this.props.user && this.props.user.loggedOut) {
      return (
        <LoggedOutFollowButton
          addText={this.props.addText || this.buttonText()}
          unstyled={this.props.unstyled}
          modalOpen={this.state.modalOpen}
          followParams={this.props.followParams}
          outlined={this.props.outlined}
        />
      );
    }

    return (
      <FollowButton
        addText={this.props.addText || this.buttonText()}
        addedText={this.props.addedText}
        openModal={this.openModal}
        disabled={this.disabled()}
        isFollowing={this.isFollowing()}
        unstyled={this.props.unstyled}
        disabledTooltipText={this.props.disabledTooltipText}
        followParams={this.props.followParams}
        outlined={this.props.outlined}
      />
    );
  }

  renderModal() {
    if (this.props.user && this.props.user.loggedOut) return null;

    return (
      <FollowModal
        headerLabel={this.headerLabel()}
        isOpen={this.state.modalOpen}
        onUpdate={this.onModalUpdate}
        onRequestClose={this.closeModal}
        submit={this.handleSubmit}
        submitLabel={this.props.addText || I18n.t('discovery.addFollowToFeed.save')}
        withPriceFilters={this.props.withPriceFilters}
        withUsedFilter={this.props.withUsedFilter}
        addGearAlert={this.state.addGearAlert}
        usedOnly={this.state.usedOnly}
        filterByPrice={this.state.filterByPrice}
        priceMax={this.state.priceMax}
        priceMin={this.state.priceMin}
        readableTitle={this.readableTitle()}
      />
    );
  }

  renderBannerOrButton() {
    if (!this.props.renderWithBanner) return this.renderButton();
    if (this.props.renderWithBanner && this.disabled()) return null;

    const params = Object.keys(this.buildParams());

    if (params.includes('id')) {
      return (
        <div className="feed-button__banner">
          <h4 className="size-150 mobile-size-130 weight-bold pb-half">
            <SanitizedRender
              html={I18n.t(
                'discovery.addFollowToFeed.youAreFollowingHtml',
                {
                  type: this.getBannerFollowType(),
                  spanOpen: '<span class="tt-capitalize">',
                  spanClose: '</span>',
                },
              )}
            />
          </h4>
          <p className="size-90 mb-1">
            <SanitizedRender
              html={I18n.t(
                'discovery.addFollowToFeed.itemsMatchingHtml',
                {
                  linkOpen: `<a href=${FEED_HREF}>`,
                  linkClose: '</a>',
                  type: this.getBannerFollowType(),
                },
              )}
            />
          </p>
          {this.renderButton()}
        </div>
      );
    }

    return (
      <div className="feed-button__banner">
        <h4 className="size-150 mobile-size-130 weight-bold pb-half">
          {I18n.t('discovery.addFollowToFeed.letGearComeToYou')}
        </h4>
        <p className="size-90">
          <SanitizedRender
            html={I18n.t(
              'discovery.addFollowToFeed.followForFeedUpdatesHtml',
              {
                type: this.getBannerFollowType(),
                linkOpen: `<a href=${FEED_HREF} class="text-link">`,
                linkClose: '</a>',
              },
            )}
          />
        </p>
        {this.renderButton()}
      </div>
    );
  }

  render() {
    if (this.hidden()) return null;

    return (
      <div className="feed-button">
        {this.renderBannerOrButton()}
        {this.renderModal()}
      </div>
    );
  }
}

export default withUserContext<ExternalProps>(FollowButtonContainer);
